// Stage Mail-V2: backend snake_case ↔ frontend V2 demo camelCase mapper。
// V1 mail.ts と 並行、 demoMailData.ts の MailMessage 型 を 一部 再利用。
//
// Stage Mail-V2 では demoMailData.ts の MailMessage 型 (camelCase) を そのまま
// MailV2Page 用 に 維持 し、 API レスポンス を ここ で マッピング する。

import type {
  MailAccountV2DTO,
  MailMessageV2DTO,
  MailDraftDTO,
  MailAiJobDTO,
  MailAttachmentDTO,
  MailLabelDTO,
  MailMemberDTO,
} from "../../../api/mail-v2";

// V2 demo 側 で 既 に 定義 さ れ て いる camelCase 型 を 再利用 する ため、
// ここに import は 行 わ ず、 types.ts で 別途 import する。
// ただし ここでは 関数 シグネチャ 用 に ローカル で 簡易 型 を 定義。

// -------------------------------------------------------
// Status: V1 backend = "completed", V2 demo = "handled"
// -------------------------------------------------------

export function mapStatusBackend2V2(s: string): "unhandled" | "handling" | "handled" {
  if (s === "completed") return "handled";
  if (s === "unhandled") return "unhandled";
  return "handling";
}

export function mapStatusV22Backend(s: "unhandled" | "handling" | "handled"): string {
  if (s === "handled") return "completed";
  return s;
}

// -------------------------------------------------------
// Mailbox: V2 demo folderId (camelCase) <-> backend mailbox_name (SENT/DRAFTS/INBOX/...)
// -------------------------------------------------------

export function mapMailboxNameToFolderId(mailbox: string | null | undefined): string {
  if (!mailbox) return "inbox";
  const upper = mailbox.toUpperCase();
  if (upper === "SENT") return "sent";
  if (upper === "DRAFTS") return "drafts";
  if (upper === "TRASH") return "trash";
  if (upper === "SPAM" || upper === "JUNK") return "spam";
  if (upper === "ARCHIVE") return "archive";
  if (upper === "STARRED" || upper === "FLAGGED") return "starred";
  return "inbox";
}

export function mapFolderIdToMailboxName(folder: string): string {
  switch (folder) {
    case "sent":
      return "SENT";
    case "drafts":
      return "DRAFTS";
    case "trash":
      return "TRASH";
    case "spam":
      return "JUNK";
    case "archive":
      return "ARCHIVE";
    case "starred":
      return "STARRED";
    case "all":
      // 2026-07-23:すべてのメール 走 DB 聚合视图(排除 Trash/Junk/Drafts/Gmail All Mail)
      return "ALL";
    default:
      return "INBOX";
  }
}

// -------------------------------------------------------
// Account mapping
// -------------------------------------------------------

export type AccountV2View = {
  id: string;
  email: string;
  displayName: string;
  accountType: "personal" | "shared" | "other";
  ownerUserId: string | null;
  unreadCount: number;
  /** Mail V2.5 (2026-08-20): アカウント レベル 同期 状態 (locked plan §16.9 §D).
   *  undefined or 'ok' = 正常, 'error' = 直近 sync が 失敗 → red banner 必要. */
  lastSyncStatus: "ok" | "error" | undefined;
  lastSyncError: string | null;
  lastSyncErrorAt: string | null;
};

export function mapAccount(raw: MailAccountV2DTO): AccountV2View {
  return {
    id: raw.id,
    email: raw.email_address,
    displayName: raw.name,
    accountType: raw.account_type,
    ownerUserId: raw.owner_user_id,
    unreadCount: 0,
    lastSyncStatus: raw.last_sync_status,
    lastSyncError: raw.last_sync_error ?? null,
    lastSyncErrorAt: raw.last_sync_error_at ?? null,
  };
}

// -------------------------------------------------------
// Message mapping (snake_case -> camelCase V2 demo shape)
// -------------------------------------------------------

export type MessageV2View = {
  id: string;
  accountId: string;
  folderId: string;
  senderName: string;
  senderEmail: string;
  recipients: string[];
  subject: string;
  preview: string;
  bodyText: string;
  bodyHtml: string;
  // Stage Mail-V2 Stage 4 (2026-07-22): MailV2List 接收 MailMessage[] (demo
  // shape 含 htmlBody),为了让 MessageV2View 直接喂给 MailV2List,
  // 加 htmlBody 作为 bodyHtml 的别名(draft body_html 不带 HTML tags 时 fallback
  // 到 bodyText)。
  htmlBody: string;
  receivedAt: string;
  isRead: boolean;
  isStarred: boolean;
  status: "unhandled" | "handling" | "handled";
  labels: string[];
  hasAttachments: boolean;
  // Stage Mail-V2 Stage 3 (2026-07-22): RFC threading metadata.
  parentMessageId: string | null;
  messageId: string | null;
  referencesHeader: string | null;
  direction: "inbound" | "outbound";
  // Stage Mail-V2 (2026-07-24): Reply 收件人计算辅助字段。
  // ccRecipients: 原邮件 Cc(逗号分隔串解析),Forward/Reply-All 用。
  // replyTo / replyAllTo / replyAllCc: 后端统一算好的回复语义。
  ccRecipients: string[];
  replyToRecipients: string[];
  replyTo: string | null;
  replyAllTo: string | null;
  replyAllCc: string | null;
};

export function mapMessage(raw: MailMessageV2DTO): MessageV2View {
  const bodyHtml = raw.body_html_sanitized ?? "";
  return {
    id: raw.id,
    accountId: raw.mail_account_id,
    folderId: mapMailboxNameToFolderId(raw.mailbox_name),
    senderName: raw.from_name ?? "",
    senderEmail: raw.from_email,
    recipients: (raw.to_addresses ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    subject: raw.subject ?? "",
    preview: raw.preview ?? "",
    bodyText: raw.body_text ?? "",
    bodyHtml,
    htmlBody: bodyHtml,
    receivedAt: raw.received_at ?? raw.created_at,
    isRead: raw.is_read,
    isStarred: raw.is_important,
    status: mapStatusBackend2V2(raw.status),
    labels: [],
    hasAttachments: raw.has_attachments,
    parentMessageId: raw.parent_message_id ?? null,
    messageId: raw.message_id ?? null,
    referencesHeader: raw.references_header ?? null,
    direction: raw.direction,
    ccRecipients: (raw.cc_addresses ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    replyToRecipients: (raw.reply_to_addresses ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    replyTo: raw.reply_to ?? null,
    replyAllTo: raw.reply_all_to ?? null,
    replyAllCc: raw.reply_all_cc ?? null,
  };
}

// -------------------------------------------------------
// Draft / AI Job / Attachment / Label mappers
// -------------------------------------------------------

export type DraftV2View = {
  id: string;
  accountId: string;
  parentMessageId: string | null;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  bodyText: string;
  bodyHtml: string;
  aiGeneratedBody: string;
  smtpStatus: "draft" | "sending" | "sent" | "failed";
  smtpError: string | null;
  updatedAt: string;
};

export function mapDraft(raw: MailDraftDTO): DraftV2View {
  return {
    id: raw.id,
    accountId: raw.mail_account_id,
    parentMessageId: raw.parent_message_id,
    to: raw.to_addresses ?? "",
    cc: raw.cc_addresses ?? "",
    bcc: raw.bcc_addresses ?? "",
    subject: raw.subject ?? "",
    bodyText: raw.body_text ?? "",
    bodyHtml: raw.body_html ?? "",
    aiGeneratedBody: raw.ai_generated_body ?? "",
    smtpStatus: raw.smtp_status,
    smtpError: raw.smtp_error,
    updatedAt: raw.updated_at,
  };
}

export function mapAiJob(raw: MailAiJobDTO | null): {
  jobId: string;
  status: string;
  draftText: string;
  errorCode: string | null;
  errorMessage: string | null;
} | null {
  if (!raw) return null;
  return {
    jobId: raw.job_id,
    status: raw.status,
    draftText: raw.draft_text ?? "",
    errorCode: raw.error_code,
    errorMessage: raw.error_message,
  };
}

export function mapAttachment(raw: MailAttachmentDTO): {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  isInline: boolean;
} {
  return {
    id: raw.id,
    filename: raw.filename,
    contentType: raw.content_type,
    sizeBytes: raw.size_bytes,
    isInline: raw.is_inline,
  };
}

export function mapLabel(raw: MailLabelDTO): {
  id: string;
  name: string;
  color: string | null;
} {
  return {
    id: raw.id,
    name: raw.name,
    color: raw.color,
  };
}

export function mapMember(raw: MailMemberDTO): {
  userId: string;
  role: string;
} {
  return {
    userId: raw.user_id,
    role: raw.role,
  };
}

// Stage Mail-V2 Stage 4 (2026-07-22): 把 MailDraftDTO 适配成 MessageV2View
// 让 MailV2List 复用 Drafts folder 列表。
//
// 关键适配:
// - id = draft.id (前端 Detail 加载时通过 mailV2Api.getDraft 拉回)
// - folderId = "drafts"
// - direction = "outbound" (draft 永远是 outbound,还没发出)
// - subject = parent_subject 优先,然后是 draft.subject
//   (前端 "Re: 〇〇" 显示需要 parent.subject,否则只看到自己写的 "Re: 〇〇")
// - senderName = "" / senderEmail = to_addresses (outbound 草稿 还没 真发,没有 recipient row)
// - preview = body_text 前 80 字
// - receivedAt = updated_at
// - isRead = true (草稿 不存在 read 概念)
export function mapDraftToMessage(raw: MailDraftDTO): MessageV2View {
  const subject = raw.subject ?? "";
  const bodyText = raw.body_text ?? "";
  const bodyHtml = raw.body_html ?? "";
  return {
    id: raw.id,
    accountId: raw.mail_account_id,
    folderId: "drafts",
    senderName: "",
    senderEmail: raw.to_addresses ?? "",
    recipients: (raw.to_addresses ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    subject,
    preview: bodyText.slice(0, 80),
    bodyText,
    bodyHtml,
    htmlBody: bodyHtml || bodyText,
    receivedAt: raw.updated_at,
    isRead: true,
    isStarred: false,
    status: "unhandled",
    labels: [],
    hasAttachments: false,
    parentMessageId: raw.parent_message_id,
    messageId: null,
    referencesHeader: null,
    direction: "outbound",
    ccRecipients: [],
    replyToRecipients: [],
    replyTo: null,
    replyAllTo: null,
    replyAllCc: null,
  };
}
